import type {ChangeEvent} from 'react';

import {useThemeColors} from '@nfq/colors';

import {useFormField} from 'UI/components/action/inputs/hooks/useFormField';

import type {ValidationFunc} from 'Shared/formHelperTypes';

/**
 * Custom hook for managing TextArea input field state, validation, and form integration.
 * This hook provides a complete solution for textarea inputs by combining form field management,
 * theme colors, validation handling, and change event processing. It serves as the primary interface
 * between TextArea components and the underlying form system.
 *
 * @param name         The name identifier for the form field, used for form registration and validation.
 * @param initialValue The initial value to set in the textarea field when the component mounts.
 * @param validation   Optional validation function to validate the textarea input value.
 * @param onChange     Callback function that handles change events from the textarea input element.
 * @returns An object containing colors, error state, field reference, form ID, change handler, and current value.
 *
 * @example
 * ```tsx
 * const {
 *     colors,
 *     error,
 *     fieldRef,
 *     formId,
 *     handleChange,
 *     value
 * } = useTextAreaInput(
 *     'description',
 *     'Initial description text',
 *     (value) => value.length > 10 ? null : 'Description must be longer than 10 characters',
 *     (e) => console.log('TextArea changed:', e.target.value)
 * );
 * ```
 */
export const useTextAreaInput = (
    name: string,
    initialValue: string,
    validation: (ValidationFunc | undefined),
    onChange: (e: ChangeEvent<HTMLTextAreaElement>) => void
) => {
    const colors = useThemeColors();
    const {
        error,
        fieldRef,
        formId,
        handleFieldChange,
        value
    } = useFormField<string, HTMLTextAreaElement>(name, initialValue, initialValue, false, validation);

    /**
     * Handles input change events by extracting the target value and calling both external and internal change handlers.
     * This function serves as a bridge between the external onChange callback and the internal form field management,
     * ensuring that both the parent component and the form field state are properly updated when the input value changes.
     *
     * @param e The change event from the HTML input element containing the new value.
     */
    const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const {value: targetValue} = e.target;

        onChange(e);

        handleFieldChange(targetValue);
    };

    return {
        colors,
        error,
        fieldRef,
        formId,
        handleChange,
        value
    };
};